import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, Loader2, PlayCircle, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function LabEmailIntake() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [polling, setPolling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [password, setPassword] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/lab/email-intake").then((r) => setCfg(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...cfg };
      if (password) body.imap_password = password;
      await api.put("/lab/email-intake", body);
      setPassword("");
      toast.success("Email intake settings saved");
      load();
    } catch (err) { toast.error(apiError(err, "Save failed")); }
    finally { setSaving(false); }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      await api.post("/lab/email-intake/poll");
      toast.success("Polling queued — reports will appear shortly");
      setTimeout(load, 4000);
    } catch { toast.error("Poll failed"); }
    finally { setPolling(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const body = {
        imap_host: cfg.imap_host,
        imap_port: cfg.imap_port,
        imap_user: cfg.imap_user,
        folder: cfg.folder,
      };
      if (password) body.imap_password = password;
      const r = await api.post("/lab/email-intake/test", body);
      if (r.data.ok) {
        toast.success(`Connected — ${r.data.unseen_count ?? 0} unread message(s) waiting`);
      } else {
        toast.error(r.data.message || "Connection failed");
      }
    } catch (err) { toast.error(apiError(err, "Test failed")); }
    finally { setTesting(false); }
  };

  if (loading || !cfg) {
    return <DashboardLayout role="lab_admin"><div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout role="lab_admin">
      <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><Mail className="w-6 h-6 text-efgh-blue" /> Email Intake</h1>
      <p className="text-ink/60">Automatically ingest reports emailed to a dedicated mailbox. We poll every 5 minutes.</p>

      {/* Status card */}
      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        <StatCard label="Status" value={cfg.enabled ? "Active" : "Disabled"} tone={cfg.enabled ? "success" : "muted"} icon={cfg.enabled ? CheckCircle2 : AlertCircle} />
        <StatCard label="Reports processed" value={cfg.processed_count || 0} tone="info" />
        <StatCard label="Last poll" value={cfg.last_poll ? new Date(cfg.last_poll).toLocaleString() : "Never"} tone="muted" small />
      </div>

      {cfg.last_status && cfg.last_status !== "ok" && (
        <Card className="mt-4 p-4 rounded-2xl bg-warning-light border-warning/40">
          <div className="text-sm text-warning font-medium">Last poll issue: <code className="text-xs">{cfg.last_status}</code></div>
        </Card>
      )}

      <Card className="mt-6 p-6 rounded-2xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading font-semibold text-ink text-lg">IMAP configuration</div>
            <p className="text-sm text-ink/60">Point us at a mailbox — any PDF attachment on an unread email becomes a Smart Report.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink/60">{cfg.enabled ? "Enabled" : "Disabled"}</span>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="intake-enabled" />
          </div>
        </div>

        <form onSubmit={save} className="mt-5 grid grid-cols-2 gap-4">
          <div><Label>IMAP host</Label><Input required data-testid="intake-host" value={cfg.imap_host} onChange={(e) => setCfg({ ...cfg, imap_host: e.target.value })} placeholder="imap.gmail.com" /></div>
          <div><Label>Port</Label><Input required type="number" value={cfg.imap_port} onChange={(e) => setCfg({ ...cfg, imap_port: parseInt(e.target.value) || 993 })} /></div>
          <div><Label>Username / Email</Label><Input required data-testid="intake-user" value={cfg.imap_user} onChange={(e) => setCfg({ ...cfg, imap_user: e.target.value })} placeholder="reports@yourlab.com" /></div>
          <div>
            <Label>Password / App Password</Label>
            <Input type="password" data-testid="intake-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={cfg.has_password ? "•••••••• (leave blank to keep)" : "App password"} />
          </div>
          <div><Label>Folder</Label><Input value={cfg.folder} onChange={(e) => setCfg({ ...cfg, folder: e.target.value })} placeholder="INBOX" /></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t border-border mt-2">
            <Button type="button" variant="outline" onClick={testConnection} disabled={testing || !cfg.imap_host || !cfg.imap_user} data-testid="test-connection">
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Test connection
            </Button>
            <Button type="button" variant="outline" onClick={pollNow} disabled={polling || !cfg.enabled} data-testid="poll-now">
              {polling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlayCircle className="w-4 h-4 mr-2" />}Poll now
            </Button>
            <Button type="submit" disabled={saving} data-testid="save-intake" className="bg-ink hover:bg-ink/90 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save configuration
            </Button>
          </div>
        </form>
      </Card>

      <Card className="mt-4 p-6 rounded-2xl bg-muted/30">
        <div className="font-heading font-semibold text-ink text-sm">💡 Setup tips</div>
        <ul className="mt-2 space-y-1 text-sm text-ink/70 list-disc list-inside">
          <li><b>Gmail:</b> Use an <a className="underline" target="_blank" rel="noreferrer" href="https://myaccount.google.com/apppasswords">App Password</a>, not your normal password. Host: <code>imap.gmail.com</code>, Port: 993.</li>
          <li><b>Outlook/Office365:</b> Host <code>outlook.office365.com</code>, Port 993. Enable IMAP in Settings first.</li>
          <li>We only read <b>unread</b> messages, and mark them as read after processing.</li>
          <li>The email subject is used as the patient name — configure your LIS to include it, or leave a default.</li>
        </ul>
      </Card>
    </DashboardLayout>
  );
}

function StatCard({ label, value, tone, icon: Icon, small }) {
  const colors = {
    success: "bg-success-light text-success",
    info: "bg-efgh-blue/10 text-efgh-blue",
    muted: "bg-muted text-ink/60",
  }[tone] || "bg-muted text-ink/60";
  return (
    <Card className="p-4 rounded-2xl">
      <div className={`w-9 h-9 rounded-lg ${colors} grid place-items-center`}>{Icon ? <Icon className="w-4 h-4" /> : <div />}</div>
      <div className={`mt-3 font-heading font-bold text-ink ${small ? "text-sm" : "text-2xl"}`}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-ink/50 mt-1">{label}</div>
    </Card>
  );
}
